import * as THREE from 'three';
import { fragmentShader, vertexShader } from './shaders.js';
import type { VisualsInterface } from './types.js';

// 质量档位 → 渲染分辨率倍率。低质量时以更少的像素渲染，直接减少片元着色器负担。
const QUALITY_SCALE: Record<string, number> = { low: 0.5, medium: 0.75, high: 1.0 };

export const Visuals: VisualsInterface = {
  renderer: null,
  scene: null,
  camera: null,
  material: null,

  init() {
    const container = document.getElementById('container');
    if (!container) return;

    this.renderer = new THREE.WebGLRenderer({ antialias: false });
    // 限制设备像素比上限为 2，避免在高 DPI 屏上像素数暴涨（3x/4x 屏会拖垮着色器）
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        u_time: { value: 0 },
        u_res: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        u_skyColor: { value: new THREE.Color() },
        u_cloudColor: { value: new THREE.Color() },
        u_rainIntensity: { value: 0.0 },
        u_snowIntensity: { value: 0.0 },
        u_lightningFlash: { value: 0.0 },
        u_quality: { value: 1.0 },
      },
      vertexShader,
      fragmentShader,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(mesh);

    // 用真实绘制缓冲区尺寸初始化 u_res，保证着色器 uv 归一化正确
    this.syncResolution();

    window.addEventListener('resize', () => {
      this.resize(window.innerWidth, window.innerHeight);
    });
  },

  update(time: number) {
    if (this.material) {
      this.material.uniforms.u_time.value = time;
    }
  },

  // 让 u_res 跟随渲染器的实际绘制缓冲区尺寸（已含 pixelRatio 与质量倍率）
  syncResolution() {
    if (this.renderer && this.material) {
      const size = new THREE.Vector2();
      this.renderer.getDrawingBufferSize(size);
      this.material.uniforms.u_res.value.set(size.x, size.y);
    }
  },

  resize(width: number, height: number) {
    if (this.renderer && this.material) {
      this.renderer.setSize(width, height);
      this.syncResolution();
    }
  },

  applyQuality(quality: string) {
    if (!this.renderer) return;
    const scale = QUALITY_SCALE[quality] ?? 1.0;
    // 以 devicePixelRatio × 质量倍率 作为最终像素比，低档位直接降低渲染像素数
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2) * scale);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.syncResolution();
  },
};
